//Include Both Helper File with needed methods

import { postVendorRegister, getRegistrationCode, getAffiliateRegistrationCode, postAffiliateRegister } from "../../../helpers/backend_helper"

// action
import {
  registerUserSuccessful,
  registerUserFailed,
  resetRegisterFlagChange,
  registrationCodeRequest,
  registrationCodeSuccess,
  registrationCodeError,
  resetRegistrationCodeFlag,
  // apiErrorChange
} from "./reducer"

const DEFAULT_REGISTRATION_ERROR = "Registration failed. Please try again."

type ParsedRegistrationError = {
  message: string
  messages: string[]
  errors?: any
}

const formatFieldName = (field: string) =>
  field.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())

const normalizeErrorMessages = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.reduce<string[]>((messages, item) => {
      messages.push(...normalizeErrorMessages(item))
      return messages
    }, [])
  }

  if (typeof value === "string" && value.trim()) {
    return [value]
  }

  if (value?.message && typeof value.message === "string") {
    return [value.message]
  }

  return []
}

export const parseRegistrationError = (
  error: any,
  fallback = DEFAULT_REGISTRATION_ERROR
): ParsedRegistrationError => {
  const source = error?.response?.data || error?.response || error
  const errors = source?.errors || error?.errors
  const fieldMessages: string[] = []

  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    Object.entries(errors).forEach(([field, messages]) => {
      normalizeErrorMessages(messages).forEach((message) => {
        fieldMessages.push(`${formatFieldName(field)}: ${message}`)
      })
    })
  }

  const directMessages = normalizeErrorMessages(errors)
  const message =
    typeof source === "string"
      ? source
      : typeof source?.message === "string"
      ? source.message
      : typeof source?.error === "string"
      ? source.error
      : typeof error?.message === "string"
      ? error.message
      : fallback

  const messages = fieldMessages.length
    ? fieldMessages
    : directMessages.length
    ? directMessages
    : [message || fallback]

  return {
    message: messages[0] || fallback,
    messages,
    errors,
  }
}

// Is user register successfull then direct plot user in redux.
export const registerUser = (user: any) => async (dispatch: any) => {
  try {
    const response = await postVendorRegister(user)
    const data: any = response

    if (data.vendor || data.status === "pending_approval" || data.success === true) {
      dispatch(registerUserSuccessful(data))
      return { success: true, vendor: data.vendor, data }
    } else {
      const parsedError = parseRegistrationError(data)
      dispatch(registerUserFailed(parsedError.message))
      return { success: false, ...parsedError }
    }
  } catch (error: any) {
    const parsedError = parseRegistrationError(error)
    dispatch(registerUserFailed(parsedError.message))
    return { success: false, ...parsedError }
  }
}
export const registerAffiliateUser = (user: any) => async (dispatch: any) => {
  try {
    let response = await postAffiliateRegister(user)
    const data: any = response

    if (data.success === true) {
      dispatch(registerUserSuccessful(data))
      return data // Return the full response data on success
    } else {
      const parsedError = parseRegistrationError(data)
      dispatch(registerUserFailed(parsedError.message))
      return { success: false, ...parsedError }
    }
  } catch (error: any) {
    const parsedError = parseRegistrationError(error)
    dispatch(registerUserFailed(parsedError.message))
    return { success: false, ...parsedError }
  }
}

export const resetRegisterFlag = () => {
  try {
    const response = resetRegisterFlagChange()
    return response
  } catch (error) {
    return error
  }
}

export const sendRegistrationCode = (email: string) => async (dispatch: any) => {
  try {
    dispatch(registrationCodeRequest())

    const response = await getRegistrationCode({ email })

    if (response?.success) {
      dispatch(registrationCodeSuccess(response as any))
      return { success: true, message: response.message }
    }

    // API returned 2xx but with error content e.g. {"errors":{"email":["The email has already been taken."]}}
    const fieldError = response?.errors?.email?.[0]
      || (response?.errors ? (Object.values(response.errors) as string[][])[0]?.[0] : null)
    const fallbackMsg = fieldError || response?.message || "Failed to send verification code"
    dispatch(registrationCodeError(fallbackMsg))
    return { success: false, message: fallbackMsg }
  } catch (error: any) {
    const fieldError = error?.errors?.email?.[0]
      || (error?.errors ? (Object.values(error.errors) as string[][])[0]?.[0] : null)
    const errorMessage = fieldError || error?.message || "Failed to send verification code"
    dispatch(registrationCodeError(errorMessage))
    return { success: false, message: errorMessage }
  }
}
export const sendAffiliateRegistrationCode = (email: string) => async (dispatch: any) => {
  try {
    dispatch(registrationCodeRequest())

    const response = await getAffiliateRegistrationCode({ email })

    if (response) {
      // cast to any to satisfy action payload typing when the action expects void/union
      dispatch(registrationCodeSuccess(response as any))
      return response
    }
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || error?.message || "Failed to send registration code"
    dispatch(registrationCodeError(errorMessage))
    throw error
  }
}

export const resetRegistrationCode = () => {
  try {
    const response = resetRegistrationCodeFlag()
    return response
  } catch (error) {
    return error
  }
}

// export const apiError = () => async (dispatch : any) => {
//   try {
//     const response = dispatch(apiErrorChange(false));
//     return response;
//   } catch (error) {
//     return error;
//   }
// };
